import { Component, EventEmitter, OnInit, Output, ViewChild, ViewContainerRef } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { interval, Subscription } from "rxjs";
import { Router } from "@angular/router";

import { SharedModule } from "../../modules/shared/shared.module";
import { OtpInputComponent } from "../otp-input/otp-input.component";
import { ModalComponent } from "../modal/modal.component";
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component";

import { ApiService } from "../../services/api.service";
import { AuthService } from "../../services/auth.service";
import { ToasterService } from "../../services/toaster.service";
import { LoadingService } from "../../services/loading.service";
import { LocalStorageService } from "../../services/local-storage.service";
import { MenuService } from "../../services/menu.service";
import { PermissionService } from "../../services/permission.service";

import { LoginRequest } from "../../models/login-request.interface";
import { Menu } from "../../models/sidebar-menu.interface";
import { StorageKeys } from "../../constants/app-constants";
import { SpinnerLoaderComponent } from "../spinner-loader/spinner-loader.component";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";

@Component({
  selector: "app-login",
  standalone: true,
  imports: [SharedModule, OtpInputComponent, SpinnerLoaderComponent, SvgIconComponent],
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"]
})
export class LoginComponent implements OnInit {
  emailLoginForm: FormGroup;
  phoneLoginForm: FormGroup;
  loginMethod: "email" | "phone" = "email";

  isOtpGenerated = false;
  otp = "";
  resendOtpBufferTime = 30;
  countdown = 0;
  resendAllowed = false;
  loggedInUserId = "";
  countdownSubscription: Subscription = new Subscription();
  isLoading: boolean = false;

  @ViewChild(OtpInputComponent) otpInputComponent!: OtpInputComponent;
  @Output() menuItemsEmitter = new EventEmitter<Menu[]>();

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private authService: AuthService,
    private toaster: ToasterService,
    private loading: LoadingService,
    private router: Router,
    private storage: LocalStorageService,
    private menuService: MenuService,
    private permissionService: PermissionService,
    private viewContainerRef: ViewContainerRef
  ) {
    this.emailLoginForm = this.createEmailForm();
    this.phoneLoginForm = this.createPhoneForm();
  }

  ngOnInit(): void {
    this.loading.loading$.subscribe({
      next: data => {
        this.isLoading = data.isLoading && data.type === "inline";
      }
    })
    this.loadStoredLoginMethod();
  }

  private createEmailForm(): FormGroup {
    return this.fb.group({
      username: ["", Validators.required],
      password: ["", Validators.required]
    });
  }

  private createPhoneForm(): FormGroup {
    const form = this.fb.group({
      mobile: ["", [Validators.required, Validators.pattern("^[0-9]{10}$")]],
      otp: ["", [Validators.required, Validators.pattern("^[0-9]{6}$")]]
    });
    form.get("mobile")?.valueChanges.subscribe(() => this.isOtpGenerated = false);
    return form;
  }

  private async loadStoredLoginMethod() {
    const stored: any = await this.storage.getItem(StorageKeys.LOGIN_METHOD);
    this.loginMethod = stored?.selected ?? "email";
    if (!stored) {
      this.storage.setItem("loginMethod", { selected: "email" });
    }
  }

  switchLoginMethod(method: "email" | "phone"): void {
    this.loginMethod = method;
    this.storage.setItem("loginMethod", { selected: method });
  }

  requestOtp(): void {
    if (!this.phoneLoginForm.get("mobile")?.valid) {
      this.phoneLoginForm.get("mobile")?.markAsTouched();
      return;
    }

    const mobile = this.phoneLoginForm.get("mobile")?.value;
    this.loading.show();
    this.api.sendOtp(mobile).subscribe({
      next: (res) => {
        this.toaster.success(res.data);
        this.isOtpGenerated = true;
        this.startCountdown();
        this.loading.hide();
      },
      error: (err) => {
        this.handleError(err);
        this.loading.hide();
      }
    });
  }

  resendOtp(): void {
    if (!this.resendAllowed) return;
    this.phoneLoginForm.get("otp")?.reset();
    this.otpInputComponent.otpForm.reset();
    this.requestOtp();
  }

  startCountdown(): void {
    this.resendAllowed = false;
    this.countdown = this.resendOtpBufferTime;
    this.countdownSubscription.unsubscribe();
    this.countdownSubscription = interval(1000).subscribe(() => {
      if (--this.countdown <= 0) {
        this.resendAllowed = true;
        this.countdownSubscription.unsubscribe();
      }
    });
  }

  formatTime(seconds: number): string {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${this.padZero(min)}:${this.padZero(sec)}`;
  }

  padZero(val: number): string {
    return val < 10 ? `0${val}` : val.toString();
  }

  onOtpChange(otp: string): void {
    this.phoneLoginForm.get("otp")?.setValue(otp);
  }

  submitEmailLogin(killExisting = false): void {
    this.handleLogin(this.emailLoginForm, {
      username: this.emailLoginForm.get("username")?.value?.toLowerCase(),
      password: this.emailLoginForm.get("password")?.value,
      killExistingSession: killExisting
    }, () => this.submitEmailLogin(true));
  }

  submitPhoneLogin(killExisting = false): void {
    this.handleLogin(this.phoneLoginForm, {
      mobile: this.phoneLoginForm.get("mobile")?.value,
      otp: this.phoneLoginForm.get("otp")?.value,
      killExistingSession: killExisting
    }, () => this.submitPhoneLogin(true));
  }

  private handleLogin(form: FormGroup, request: LoginRequest, retryCallback: () => void): void {
    form.markAllAsTouched();
    if (!form.valid) return;

    this.loading.show('inline');
    this.authService.login(request).subscribe({
      next: async (res) => {
        if (res?.data && !res.data.success) {
          this.showSessionDialog(retryCallback);
        } else {
          this.toaster.success("Login successful!");
          await this.saveUserProfile();
          this.menuService.fetchAndStoreMenuItems(this.loggedInUserId);
          await this.router.navigateByUrl("/admin/manage-users");
        }
        this.loading.hide();
      },
      error: (err) => this.handleError(err)
    });
  }

  private showSessionDialog(confirmAction: () => void): void {
    const modalRef = this.viewContainerRef.createComponent(ModalComponent);
    modalRef.instance.title = "Session Already Active";
    const dialogRef = modalRef.instance.loadComponent(ConfirmDialogComponent);
    dialogRef.instance.message = "An active session is currently in progress. Would you like to terminate it and start a new session?";
    dialogRef.instance.confirm.subscribe(() => confirmAction());
    dialogRef.instance.cancel.subscribe(() => modalRef.destroy());
  }

  private handleError(err: any): void {
    console.error("Login error", err);
    if (err.status === 400) {
      const desc = err.error.errorDesc || "Unknown Error!";
      const attempts = parseInt(err.error.data?.remainingAttempts ?? "0");

      if (desc.includes("Invalid email") || desc.includes("Incorrect password") || desc.includes("Incorrect OTP")) {
        if (attempts === 0) {
          this.toaster.error(`Account locked. Please contact administrator.`);
        } else {
          this.toaster.error(`Invalid login attempt. ${attempts} attempt${attempts === 1 ? "" : "s"} left!`);
        }
      } else {
        this.toaster.error(desc);
      }
    } else {
      this.toaster.error(err.error?.errorDesc || "Login failed.");
    }

    setTimeout(() => this.loading.hide(), 1000);
  }

  async saveUserProfile(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.api.getMyProfile().subscribe({
        next: (res) => {
          this.loggedInUserId = res.data.id;
          this.storage.setItem(StorageKeys.USER_PROFILE, JSON.stringify(res.data));
          this.permissionService.setPermissions(res.data?.permissions);
          resolve();
        },
        error: reject
      });
    });
  }

  navigate(route: string): void {
    this.router.navigate([route]);
  }
}
